import {
  forwardRef,
  useCallback,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { CloseIcon, ErrorSolidIcon } from '@icons';
import { FormLabel } from '..';
import {
  ErrorMessage,
  FormInputContainer,
  TagChip,
  TagInput,
  TagRemove,
  TagText,
  TagsField,
} from './InputTag.style';

export type InputTagDelimiter = 'Enter' | 'Comma' | 'Tab';

export interface InputTagProps {
  id?: string;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  label?: string;
  errorText?: string;
  placeholder?: string;
  tooltip?: {
    title: string;
    content: string;
  };
  /** Teclas que confirmam a palavra atual como tag (padrão: Enter, vírgula e Tab). */
  delimiters?: InputTagDelimiter[];
  /** Ignora tags duplicadas (padrão: true). */
  onlyUnique?: boolean;
  maxTags?: number;
  validateTag?: (tag: string) => boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/** Mantém apenas caracteres de letra (Unicode), incluindo acentos. */
function lettersOnly(value: string): string {
  return value.replace(/[^\p{L}]/gu, '');
}

function splitTags(raw: string): string[] {
  return raw
    .split(/[,\n\r]+/)
    .map((s) => lettersOnly(s.trim()))
    .filter(Boolean);
}

function isVisibleTag(tag: string): boolean {
  return lettersOnly(tag.trim()).length > 0;
}

export interface InputTagHandle {
  focus: () => void;
}

const FormInputTag = forwardRef<InputTagHandle, InputTagProps>(
  (
    {
      id,
      tags,
      onTagsChange,
      label,
      errorText,
      placeholder = 'Digite e pressione Enter',
      tooltip,
      delimiters = ['Enter', 'Comma', 'Tab'],
      onlyUnique = true,
      maxTags,
      validateTag,
      disabled,
      readOnly,
      className,
      'aria-invalid': ariaInvalid,
      'aria-describedby': ariaDescribedBy,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const generatedId = useId();
    const [draft, setDraft] = useState('');

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    const pushTags = useCallback(
      (incoming: string[]) => {
        if (incoming.length === 0) return;
        let next = [...tags];
        for (const piece of incoming) {
          const cleaned = lettersOnly(piece);
          if (!cleaned) continue;
          if (maxTags != null && next.length >= maxTags) break;
          if (onlyUnique && next.includes(cleaned)) continue;
          if (validateTag && !validateTag(cleaned)) continue;
          next.push(cleaned);
        }
        if (next.length !== tags.length || next.some((t, i) => t !== tags[i])) {
          onTagsChange(next);
        }
      },
      [tags, onTagsChange, onlyUnique, maxTags, validateTag]
    );

    const removeAt = useCallback(
      (index: number) => {
        onTagsChange(tags.filter((_, i) => i !== index));
      },
      [tags, onTagsChange]
    );

    const commitDraft = useCallback(() => {
      const pieces = splitTags(draft);
      if (pieces.length === 0) return;
      pushTags(pieces);
      setDraft('');
    }, [draft, pushTags]);

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (readOnly || disabled) return;

      if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
        e.preventDefault();
        removeAt(tags.length - 1);
        return;
      }

      const isDelimiter =
        (e.key === 'Enter' && delimiters.includes('Enter')) ||
        (e.key === ',' && delimiters.includes('Comma')) ||
        (e.key === 'Tab' && delimiters.includes('Tab') && draft.trim() !== '');

      if (isDelimiter && draft.trim() !== '') {
        e.preventDefault();
        commitDraft();
      }
    };

    const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
      if (readOnly || disabled) return;
      const text = e.clipboardData.getData('text');
      if (!/[,\n\r]/.test(text)) return;
      e.preventDefault();
      const fromDraft = draft.trim() ? [draft.trim(), ...splitTags(text)] : splitTags(text);
      pushTags(fromDraft);
      setDraft('');
    };

    const fieldId = id ?? generatedId;
    const hasVisibleTags = tags.some(isVisibleTag);

    return (
      <FormInputContainer className={className}>
        {label && (
          <FormLabel htmlFor={fieldId} tooltip={tooltip}>
            {label}
          </FormLabel>
        )}
        <TagsField
          $hasError={!!errorText}
          $disabled={!!disabled}
          onMouseDown={(ev) => {
            const t = ev.target as HTMLElement;
            if (t === inputRef.current || t.closest('[data-tag]')) return;
            ev.preventDefault();
            inputRef.current?.focus();
          }}
        >
          {tags.map((tag, index) => {
            if (!isVisibleTag(tag)) return null;
            return (
              <TagChip key={`${tag}-${index}`} data-tag>
                <TagText title={tag}>{tag}</TagText>
                {!readOnly && !disabled && (
                  <TagRemove
                    type="button"
                    aria-label={`Remover tag ${tag}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => removeAt(index)}
                  >
                    <CloseIcon />
                  </TagRemove>
                )}
              </TagChip>
            );
          })}
          <TagInput
            ref={inputRef}
            id={fieldId}
            value={draft}
            onChange={(e) => setDraft(lettersOnly(e.target.value))}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onBlur={commitDraft}
            placeholder={!hasVisibleTags ? placeholder : undefined}
            disabled={disabled}
            readOnly={readOnly}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
          />
        </TagsField>
        {errorText && (
          <ErrorMessage>
            <ErrorSolidIcon /> <span>{errorText}</span>
          </ErrorMessage>
        )}
      </FormInputContainer>
    );
  }
);

FormInputTag.displayName = 'FormInputTag';

export default FormInputTag;
